import React from 'react';
import { Users, LogIn, X, User, BookOpen, Settings } from 'lucide-react';
import { User as FirebaseUser } from 'firebase/auth';
import { logout } from '../lib/auth';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';

interface SidebarProps {
  user: FirebaseUser | null;
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ user, isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  
  const handleLogout = async () => {
    const { error } = await logout();
    if (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div 
      className={cn(
        "fixed inset-y-0 left-0 transform w-64 bg-card border-r transition-transform duration-200 ease-in-out z-30",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b">
        <h1 className="text-xl font-bold text-primary">Neurosaber</h1>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex flex-col h-[calc(100%-4rem)]">
        <div className="p-4">
          <div className="flex items-center space-x-4 mb-6">
            <Avatar>
              <AvatarFallback>
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <p className="text-sm font-medium leading-none">{user?.email}</p>
              <p className="text-xs text-muted-foreground">Administrador</p>
            </div>
          </div>

          <nav className="space-y-2">
            <Link
              to="/matriculas"
              className={cn(
                "flex items-center w-full px-4 py-2 rounded-lg transition-colors",
                isActive('/matriculas') || isActive('/')
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Users className="w-5 h-5 mr-4" />
              Matrículas
            </Link>

            <Link
              to="/campos"
              className={cn(
                "flex items-center w-full px-4 py-2 rounded-lg transition-colors",
                isActive('/campos')
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <BookOpen className="w-5 h-5 mr-4" />
              Campos
            </Link>

            <Link
              to="/configuracoes"
              className={cn(
                "flex items-center w-full px-4 py-2 rounded-lg transition-colors",
                isActive('/configuracoes')
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Settings className="w-5 h-5 mr-4" />
              Configurações
            </Link>

            <Link
              to="/perfil"
              className={cn(
                "flex items-center w-full px-4 py-2 rounded-lg transition-colors",
                isActive('/perfil')
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <User className="w-5 h-5 mr-4" />
              Perfil
            </Link>
          </nav>
        </div>

        <div className="mt-auto">
          <Separator />
          <Button 
            variant="ghost" 
            className="w-full flex items-center justify-start px-8 py-4 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
          >
            <LogIn className="w-5 h-5 mr-4" />
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
}